import { useCallback, useContext, useMemo } from 'react';
import { Card, Collapse, ListGroup } from 'react-bootstrap';
import { MdClearAll, MdClose, MdPieChart } from 'react-icons/md';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton/IconButton';
import { Appearance, Intent, Size } from '@Components/designConstants';
import { useMap } from '../../utils/hooks/useData';
import { NonCartContext } from './noncart-context';
import {
  Placement,
  RotationItem,
  StreamingCreativesContext,
  TabKey
} from './streaming-creatives-constants';
import { makeNetPlatKey } from './streaming-creatives-utils';

export const SideCart = () => {
  const {
    selectedMap,
    shopData,
    workspaceOptions,
    workspaceMap,
    setSelectedMap,
    goToTab,
    cart,
    addToCart,
    setWorkspaceMap,
    setPlatform,
    workspaceMetadataMap,
    setWorkspaceMetadataMap,
    setLength,
    setDimensions,
    setAdServer,
    setFlashtalkingPlacementType
  } = useContext(StreamingCreativesContext);

  const { setViewPlacementId } = useContext(NonCartContext);
  const [expandMap, setExpandValue] = useMap<string, boolean>();

  const rotation = useMemo(() => {
    const result: RotationItem[] = [];
    for (const { isci, color, reason } of workspaceOptions) {
      const value = workspaceMap[isci];
      const { audioCompanion, clickthroughURL } = workspaceMetadataMap[isci] || {};
      if (value && !reason) {
        result.push({ isci, value, color, audioCompanion, clickthroughURL });
      }
    }
    return result;
  }, [workspaceOptions, workspaceMap, workspaceMetadataMap]);

  const items = useMemo(() => {
    const selected: Placement[] = [];
    for (const id of Object.keys(selectedMap)) {
      if (selectedMap[id] && shopData.placementMap[id]) {
        selected.push(shopData.placementMap[id]);
      }
    }
    const groups: Record<string, Placement[]> = {};
    for (const placement of selected) {
      const key = makeNetPlatKey(placement);
      (groups[key] ||= []).push(placement);
    }
    return Object.values(groups).sort((a, b) =>
      makeNetPlatKey(a[0]).localeCompare(makeNetPlatKey(b[0]))
    );
  }, [selectedMap, shopData]);

  const [sumTo100, buttonText] = useMemo(() => {
    const pctSum = rotation.reduce((sum, item) => sum + item.value, 0);
    let text = 'Add to Cart';
    if (pctSum > 100) {
      text = `Remove ${pctSum - 100}% from your rotation.`;
    }
    if (pctSum < 100) {
      text = `Add ${100 - pctSum}% to your rotation.`;
    }
    return [pctSum === 100, text];
  }, [rotation]);

  const clearGatingValues = useCallback(() => {
    setPlatform('');
    setAdServer('');
    setDimensions('');
    setLength(null);
    setFlashtalkingPlacementType('');
  }, [setAdServer, setDimensions, setFlashtalkingPlacementType, setLength, setPlatform]);

  const ourAddToCart = useCallback(() => {
    addToCart({
      id: Date.now(),
      placements: items
        .flat()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(placement => placement.id),
      rotation: rotation.map(({ isci, value, audioCompanion, clickthroughURL }) => ({
        isci,
        value,
        audioCompanion,
        clickthroughURL
      }))
    });
    setSelectedMap({});
    setWorkspaceMap({});
    setWorkspaceMetadataMap({});
    goToTab(TabKey.MENU);
  }, [
    addToCart,
    items,
    rotation,
    setSelectedMap,
    setWorkspaceMap,
    setWorkspaceMetadataMap,
    goToTab
  ]);

  return (
    <div className='streaming-creatives-card-container side-cart'>
      <Card className='streaming-creatives-card'>
        <div className='select-list'>
          {!items.length && (
            <div className='no-tags'>No tags selected. Add them from the tag menu.</div>
          )}
          <ListGroup>
            {items.map(placementList => {
              const key = makeNetPlatKey(placementList[0]);
              const { network, platform } = placementList[0];
              return (
                <ListGroup.Item
                  key={key}
                  className='cart-list-item'
                  onClick={() => setExpandValue(key, !expandMap[key])}
                >
                  {network} {platform}
                  <IconButton
                    appearance={Appearance.EMPTY}
                    size={Size.SM}
                    className='remove-item delete-button'
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newMap = { ...selectedMap };
                      for (const { id } of placementList) {
                        newMap[id] = false;
                      }
                      setSelectedMap(newMap);
                      if (!Object.values(newMap).includes(true)) {
                        clearGatingValues();
                      }
                    }}
                  >
                    <MdClose />
                  </IconButton>
                  <Collapse in={Boolean(expandMap[key])}>
                    <ListGroup variant='flush' className='placement-list'>
                      {placementList.map(({ name, id }) => (
                        <ListGroup.Item key={name} className='placement-item'>
                          {name}
                          <IconButton
                            appearance={Appearance.EMPTY}
                            size={Size.SM}
                            className='view-placement-button'
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              setViewPlacementId(id);
                            }}
                          >
                            <MdPieChart />
                          </IconButton>
                          <IconButton
                            appearance={Appearance.EMPTY}
                            size={Size.SM}
                            className='remove-item delete-button'
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newMap = { ...selectedMap, [id]: false };
                              setSelectedMap(newMap);
                              if (!Object.values(newMap).includes(true)) {
                                clearGatingValues();
                              }
                            }}
                          >
                            <MdClose />
                          </IconButton>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </Collapse>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        </div>
        <div className='buttons-container'>
          {items.length ? (
            <>
              <IconButton
                appearance={Appearance.OUTLINED}
                design={Intent.ERROR}
                className='clear-all'
                onClick={() => {
                  setSelectedMap({});
                  clearGatingValues();
                }}
                title='Clear all selected'
              >
                <MdClearAll />
              </IconButton>
              <Button size={Size.LG} disabled={!sumTo100} onClick={ourAddToCart}>
                {buttonText}
              </Button>
            </>
          ) : (
            <Button size={Size.LG} disabled={!cart.length} onClick={() => goToTab(TabKey.CART)}>
              View Cart ({cart.length})
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};
